/**
 * useDictionary — fetches English definitions (dictionaryapi.dev / datamuse) +
 * Russian translation (MyMemory / Google) for a given word.
 * Results are cached in sessionStorage to minimise API calls.
 */

export interface DictionaryDefinition {
  partOfSpeech: string;
  definition: string;
  example?: string;
}

export interface DictionaryResult {
  word: string;
  phonetic?: string;      // IPA e.g. /həˈloʊ/
  audioUrl?: string;      // pronunciation MP3 from freedictionary
  definitions: DictionaryDefinition[];
  translation?: string;   // Russian translation
}

export interface WordAtPointResult {
  word: string;
  range: Range;
}

const CACHE_PREFIX = 'ep_dict_v3_';

/** Check sessionStorage cache */
function getCache(word: string): DictionaryResult | null {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + word.toLowerCase());
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Write sessionStorage cache */
function setCache(word: string, result: DictionaryResult): void {
  try {
    sessionStorage.setItem(CACHE_PREFIX + word.toLowerCase(), JSON.stringify(result));
  } catch {
    // Quota exceeded — silently ignore
  }
}

/** Fetch with strict timeout helper */
async function fetchWithTimeout(url: string, options: RequestInit = {}, timeoutMs = 2500): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return res;
  } finally {
    clearTimeout(timer);
  }
}

/** Fetch EN definitions from dictionaryapi.dev (fast timeout) */
async function fetchDictionaryApiDev(word: string): Promise<Pick<DictionaryResult, 'phonetic' | 'audioUrl' | 'definitions'>> {
  try {
    const res = await fetchWithTimeout(`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`, {}, 2500);
    if (!res.ok) return { definitions: [] };
    const data = await res.json();
    const entry = Array.isArray(data) ? data[0] : null;
    if (!entry) return { definitions: [] };

    // Phonetic
    const phonetic: string | undefined =
      entry.phonetic ||
      entry.phonetics?.find((p: any) => p.text)?.text;

    // Audio URL
    let audioUrl: string | undefined =
      entry.phonetics?.find((p: any) => typeof p.audio === 'string' && p.audio.trim().length > 0)?.audio;

    if (audioUrl && audioUrl.startsWith('//')) {
      audioUrl = `https:${audioUrl}`;
    }

    // Definitions — up to 3 from first meanings
    const definitions: DictionaryDefinition[] = [];
    for (const meaning of (entry.meanings ?? []).slice(0, 3)) {
      for (const def of (meaning.definitions ?? []).slice(0, 2)) {
        definitions.push({
          partOfSpeech: meaning.partOfSpeech ?? '',
          definition: def.definition ?? '',
          example: def.example,
        });
        if (definitions.length >= 3) break;
      }
      if (definitions.length >= 3) break;
    }

    return { phonetic, audioUrl, definitions };
  } catch {
    return { definitions: [] };
  }
}

const POS_MAP: Record<string, string> = {
  n: 'noun',
  v: 'verb',
  adj: 'adjective',
  adv: 'adverb',
  u: 'word',
};

/** Fallback EN definitions from Datamuse API (very fast, reliable) */
async function fetchDatamuseDefinitions(word: string): Promise<Pick<DictionaryResult, 'phonetic' | 'audioUrl' | 'definitions'>> {
  try {
    const res = await fetchWithTimeout(`https://api.datamuse.com/words?sp=${encodeURIComponent(word)}&md=dp&max=1`, {}, 2000);
    if (!res.ok) return { definitions: [] };
    const data = await res.json();
    const item = Array.isArray(data) && data.length > 0 ? data[0] : null;
    if (!item || !Array.isArray(item.defs) || item.defs.length === 0) {
      return { definitions: [] };
    }

    // Extract phonetic if available in tags
    let phonetic: string | undefined;
    if (Array.isArray(item.tags)) {
      const pronTag = item.tags.find((t: string) => t.startsWith('pron:') || t.startsWith('ipa:'));
      if (pronTag) {
        phonetic = `/${pronTag.split(':')[1]}/`;
      }
    }

    const definitions: DictionaryDefinition[] = item.defs.slice(0, 3).map((d: string) => {
      const [posCode, ...rest] = d.split('\t');
      const defText = rest.join('\t').trim();
      const partOfSpeech = POS_MAP[posCode?.trim() || ''] || posCode?.trim() || 'definition';
      return {
        partOfSpeech,
        definition: defText,
      };
    });

    return { phonetic, audioUrl: undefined, definitions };
  } catch {
    return { definitions: [] };
  }
}

/** Fetch EN definitions with primary & fallback sources */
async function fetchDefinitions(word: string): Promise<Pick<DictionaryResult, 'phonetic' | 'audioUrl' | 'definitions'>> {
  // Primary: dictionaryapi.dev
  const primary = await fetchDictionaryApiDev(word);
  if (primary.definitions.length > 0) return primary;

  // Fallback: Datamuse API
  const fallback = await fetchDatamuseDefinitions(word);
  return fallback;
}

/** Decode HTML entities like &#10; &amp; &quot; etc. returned by translation APIs */
function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Sanitise raw translation — remove junk characters and trim */
function cleanTranslation(raw: string, word: string): string | undefined {
  if (!raw) return undefined;
  const decoded = decodeHtmlEntities(raw);
  // Reject if translation is just the original word (untranslated)
  if (decoded.toLowerCase() === word.toLowerCase()) return undefined;
  // Reject if it contains placeholder patterns like "QUERY LENGTH LIMIT"
  if (/QUERY|LIMIT|ERROR|NO QUERY/i.test(decoded)) return undefined;
  // Keep only the first sentence / first comma-separated value if very long
  const trimmed = decoded.split(/[;\n]/)[0]?.trim() ?? decoded;
  return trimmed || undefined;
}

/** Fetch translation via MyMemory API (fast, reliable) */
async function fetchMyMemoryTranslation(word: string): Promise<string | undefined> {
  try {
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(word)}&langpair=en|ru&de=app@englishpatient.org`;
    const res = await fetchWithTimeout(url, {}, 2500);
    if (!res.ok) return undefined;
    const data = await res.json();
    const t = data?.responseData?.translatedText;
    const cleaned = cleanTranslation(t ?? '', word);
    if (cleaned) return cleaned;
    // Fallback: first match from MyMemory
    const match = data?.matches?.[0]?.translation;
    return match ? cleanTranslation(match, word) : undefined;
  } catch {
    return undefined;
  }
}

/** Try Google Translate unofficial endpoint (short timeout fallback) */
async function fetchGoogleTranslation(word: string): Promise<string | undefined> {
  try {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=ru&dt=t&q=${encodeURIComponent(word)}`;
    const res = await fetchWithTimeout(url, {}, 2000);
    if (!res.ok) return undefined;
    const data = await res.json();
    // Response: [[[translatedText, original, ...],...],...]
    const parts: string[] = (data?.[0] ?? [])
      .map((chunk: any[]) => chunk?.[0] ?? '')
      .filter(Boolean);
    const combined = parts.join('').trim();
    return cleanTranslation(combined, word);
  } catch {
    return undefined;
  }
}

/** Fetch EN→RU translation — tries MyMemory first, falls back to Google */
async function fetchTranslation(word: string): Promise<string | undefined> {
  // MyMemory has lower latency and high availability
  const myMemory = await fetchMyMemoryTranslation(word);
  if (myMemory) return myMemory;

  // Fallback: Google Translate
  const google = await fetchGoogleTranslation(word);
  if (google) return google;

  return undefined;
}

/** In-flight dedup map (word → pending Promise) */
const inFlight = new Map<string, Promise<DictionaryResult | null>>();

/** Main lookup function — returns translation and definitions */
export async function lookupWord(word: string): Promise<DictionaryResult | null> {
  const clean = word.trim().toLowerCase().replace(/[^a-z'\-\s]/gi, '').trim();
  if (!clean || clean.length < 1) return null;

  // Check cache
  const cached = getCache(clean);
  if (cached) return cached;

  // Dedup in-flight
  if (inFlight.has(clean)) return inFlight.get(clean)!;

  const promise = (async (): Promise<DictionaryResult | null> => {
    // Run translation and definition lookups in parallel with fast timeouts
    const [defData, translation] = await Promise.all([
      fetchDefinitions(clean),
      fetchTranslation(clean),
    ]);

    if (defData.definitions.length === 0 && !translation) return null;

    const result: DictionaryResult = {
      word: clean,
      phonetic: defData.phonetic,
      audioUrl: defData.audioUrl,
      definitions: defData.definitions,
      translation,
    };

    setCache(clean, result);
    return result;
  })();

  inFlight.set(clean, promise);
  promise.finally(() => inFlight.delete(clean));

  return promise;
}

/** Extract the word around a click, ensuring the click coordinates fall within the word's client rect */
export function getWordAtPoint(x: number, y: number): WordAtPointResult | null {
  // ── Ground truth: what element is actually under the cursor? ──────────
  const clickedEl = document.elementFromPoint(x, y);
  if (!clickedEl) return null;

  // If the element has no text at all, bail immediately (e.g. icon, image, empty div)
  if (!clickedEl.textContent?.trim()) return null;

  let textNode: Text | null = null;
  let textOffset = 0;

  // ── Strategy: caretRangeFromPoint / caretPositionFromPoint ────────
  const range: Range | null =
    (document as any).caretRangeFromPoint?.(x, y) ??
    (() => {
      const pos = (document as any).caretPositionFromPoint?.(x, y);
      if (!pos) return null;
      const r = document.createRange();
      r.setStart(pos.offsetNode, pos.offset);
      r.setEnd(pos.offsetNode, pos.offset);
      return r;
    })();

  if (range) {
    const node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE) {
      // Accept only if the text node is actually inside the clicked element
      const parentEl = (node as Text).parentElement;
      if (parentEl && (clickedEl.contains(parentEl) || parentEl === clickedEl)) {
        textNode = node as Text;
        textOffset = range.startOffset;
      }
    }
  }

  if (!textNode) return null;

  const text = textNode.textContent ?? '';
  if (!text) return null;

  let pos = Math.min(textOffset, text.length - 1);
  if (pos < 0) return null;

  // If clicking right past a word character, look one character back
  if (!/[a-zA-Z'\-]/.test(text[pos] ?? '')) {
    if (pos > 0 && /[a-zA-Z'\-]/.test(text[pos - 1] ?? '')) {
      pos = pos - 1;
    } else {
      return null;
    }
  }

  if (!/[a-zA-Z'\-]/.test(text[pos] ?? '')) return null;

  let start = pos;
  while (start > 0 && /[a-zA-Z'\-]/.test(text[start - 1]!)) start--;
  let end = pos;
  while (end < text.length && /[a-zA-Z'\-]/.test(text[end]!)) end++;

  // Trim leading/trailing hyphens/apostrophes
  let cleanStart = start;
  let cleanEnd = end;
  while (cleanStart < cleanEnd && /[\-']/.test(text[cleanStart]!)) cleanStart++;
  while (cleanEnd > cleanStart && /[\-']/.test(text[cleanEnd - 1]!)) cleanEnd--;

  const word = text.slice(cleanStart, cleanEnd).trim();
  if (!word || !/[a-zA-Z]/.test(word)) return null;

  try {
    const wordRange = document.createRange();
    wordRange.setStart(textNode, cleanStart);
    wordRange.setEnd(textNode, cleanEnd);

    // CRITICAL: Check if click coordinates (x, y) actually fall inside the bounding box of the word
    const rects = wordRange.getClientRects();
    if (!rects || rects.length === 0) return null;

    let isInside = false;
    const TOLERANCE = 4; // Allow small 4px margin for easier touch/clicking
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      if (
        x >= r.left - TOLERANCE &&
        x <= r.right + TOLERANCE &&
        y >= r.top - TOLERANCE &&
        y <= r.bottom + TOLERANCE
      ) {
        isInside = true;
        break;
      }
    }

    if (!isInside) {
      return null;
    }

    return { word, range: wordRange };
  } catch {
    return null;
  }
}

